import { useTranslation } from 'react-i18next';
import {
  MOISTURE_DISPLAY_PLACES,
  formatDecimalForDisplay,
  type D2216Document,
} from '@lims/shared-types';
import { ReadOnlyField, Reading } from '../../components/ui/index.js';
import { formatDateTime } from '../../components/format.js';
import { currentLocale } from '../../i18n/index.js';
import { CardStateLabel, DeterminationCard, EnteredItem, SubSection } from './bench-parts.js';
import { ResultBlock } from './specialized-form/index.js';

/**
 * A FROZEN ASTM D2216 RESULT, READ-ONLY — what a reviewer and an approver
 * act on (owner specification section 9): every reading as entered, every
 * value as the server calculated it, the environment and the remarks.
 * Entered and calculated values carry different marks (CLAUDE.md section 8);
 * a value nobody recorded reads as "not recorded", never as zero.
 *
 * One card per determination, the same shape the technician filled in, so a
 * reviewer reads the record without scrolling a wide table sideways (D278).
 */
export function D2216Record({ document }: { readonly document: D2216Document }) {
  const { t } = useTranslation();
  const locale = currentLocale();
  const notRecorded = t('common.notRecorded');
  const info = document.testInformation;
  const entered = (value: string | null) => (
    <Reading value={value} provenance="entered" provenanceLabel={t('d2216.enteredLabel')} absentLabel={notRecorded} />
  );
  const calculated = (value: string | null | undefined, places: number, emphasis = false) => (
    <Reading
      value={formatDecimalForDisplay(value ?? null, places)}
      provenance="calculated"
      provenanceLabel={t('d2216.calculatedByServer')}
      absentLabel={t('d2216.notCalculated')}
      emphasis={emphasis}
    />
  );

  return (
    <div className="flex flex-col gap-stack" data-testid="d2216-record">
      <SubSection title={t('d2216.testInformationTitle')}>
        <div className="grid grid-cols-1 gap-stack sm:grid-cols-2 xl:grid-cols-4">
          <ReadOnlyField label={t('d2216.methodUsed')}>
            {info.methodUsed ? t(`d2216.method.${info.methodUsed}`) : notRecorded}
          </ReadOnlyField>
          <ReadOnlyField label={t('d2216.ovenTemperature')}>
            <span dir="ltr">{info.ovenTemperature ?? notRecorded}</span>
          </ReadOnlyField>
          <ReadOnlyField label={t('d2216.ovenStartedAt')}>
            <bdi>{formatDateTime(info.ovenStartedAt, { locale }) ?? notRecorded}</bdi>
          </ReadOnlyField>
          <ReadOnlyField label={t('d2216.nominalMaximumSize')}>
            <span dir="ltr">{info.nominalMaximumSize ?? notRecorded}</span>
          </ReadOnlyField>
        </div>
      </SubSection>

      <SubSection title={t('d2216.determinationsTitle')}>
        {document.determinations.length === 0 ? (
          <p className="text-body text-default-600" data-testid="d2216-record-table">
            {t('d2216.noDeterminations')}
          </p>
        ) : (
          <ol className="flex flex-col gap-stack" data-testid="d2216-record-table">
            {document.determinations.map((row, index) => (
              <DeterminationCard
                key={row.id}
                testId="d2216-record-row"
                ariaLabel={t('d2216.determinationNumbered', { number: index + 1 })}
                title={t('d2216.determinationNumbered', { number: index + 1 })}
                state={
                  <CardStateLabel
                    state={row.calculation ? 'calculated' : 'incomplete'}
                    label={row.calculation ? t('d2216.calculatedByServer') : t('d2216.notCalculated')}
                  />
                }
                entered={
                  <>
                    <EnteredItem label={t('d2216.containerNumber')}>{row.containerNumber ?? notRecorded}</EnteredItem>
                    <EnteredItem label={t('d2216.containerMass')}>{entered(row.containerMass)}</EnteredItem>
                    <EnteredItem label={t('d2216.containerWetMass')}>{entered(row.containerWetMass)}</EnteredItem>
                    <EnteredItem label={t('d2216.containerDryMass')}>{entered(row.containerDryMass)}</EnteredItem>
                  </>
                }
                calculated={
                  <>
                    <ResultBlock label={t('d2216.waterMass')}>
                      {calculated(row.calculation?.waterMass, MOISTURE_DISPLAY_PLACES.mass)}
                    </ResultBlock>
                    <ResultBlock label={t('d2216.drySoilMass')}>
                      {calculated(row.calculation?.drySoilMass, MOISTURE_DISPLAY_PLACES.mass)}
                    </ResultBlock>
                    <ResultBlock label={t('d2216.waterContent')}>
                      {calculated(row.calculation?.waterContentPercent, MOISTURE_DISPLAY_PLACES.waterContent, true)}
                    </ResultBlock>
                  </>
                }
              />
            ))}
          </ol>
        )}
        <p className="text-caption text-default-600">{t('d2216.recordLegend')}</p>
      </SubSection>

      <SubSection title={t('d2216.environmentTitle')}>
        <div className="grid grid-cols-1 gap-stack sm:grid-cols-2">
          <ReadOnlyField label={t('d2216.ambientTemperature')}>{entered(document.environment.ambientTemperature)}</ReadOnlyField>
          <ReadOnlyField label={t('d2216.relativeHumidity')}>{entered(document.environment.relativeHumidity)}</ReadOnlyField>
        </div>
        <ReadOnlyField label={t('d2216.remarks')}>
          <span dir="auto" className="whitespace-pre-wrap">{document.remarks ?? notRecorded}</span>
        </ReadOnlyField>
      </SubSection>
    </div>
  );
}
